/**
 * HabitForm — Form for creating a new habit with capacity enforcement.
 *
 * Features:
 * - Name input (required, 1-100 chars)
 * - Description textarea (optional, 0-500 chars)
 * - Target time picker
 * - Color picker (predefined palette)
 * - Error banner when at 30 active habits limit
 * - Archived habits section with restore option
 *
 * Uses useHabitStore for creation and restoration of archived habits.
 *
 * @see Requirements 8.1, 8.7, 8.8
 */

import { useState, useCallback, useEffect } from 'react';
import type { Habit } from '../../types';
import { useHabitStore } from '../../stores';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card } from '../ui/Card';
import { MAX_ACTIVE_HABITS } from '../../services/habitService';

export interface HabitFormProps {
  /** Called after a habit is successfully created. */
  onCreated?: (habit: Habit) => void;
  /** Called when the user cancels or closes the form. */
  onCancel?: () => void;
}

/** Predefined color palette for habit color selection. */
const COLOR_OPTIONS = [
  '#F5A5B8', // rose
  '#B4A7D6', // lavender
  '#B8D4E3', // sky blue
  '#F2D4A7', // warm gold
  '#A8D5BA', // sage green
  '#E6A8D7', // orchid
  '#FFB7B2', // coral
  '#C9B1FF', // violet
];

export function HabitForm({ onCreated, onCancel }: HabitFormProps) {
  const habits = useHabitStore((s) => s.habits);
  const createHabit = useHabitStore((s) => s.createHabit);
  const updateHabit = useHabitStore((s) => s.updateHabit);
  const loadHabits = useHabitStore((s) => s.loadHabits);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [targetTime, setTargetTime] = useState('');
  const [color, setColor] = useState(COLOR_OPTIONS[0]);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [formError, setFormError] = useState<string | null>(null);

  // Archived habits state
  const [archivedHabits, setArchivedHabits] = useState<Habit[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [isLoadingArchived, setIsLoadingArchived] = useState(false);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  // Check if at capacity
  const isAtCapacity = habits.length >= MAX_ACTIVE_HABITS;

  // Load archived habits when section is expanded
  useEffect(() => {
    if (showArchived) {
      loadArchivedHabits();
    }
  }, [showArchived]);

  const loadArchivedHabits = async () => {
    setIsLoadingArchived(true);
    try {
      const { getHabits } = await import('../../services/habitService');
      const allHabits = await getHabits(true);
      setArchivedHabits(allHabits.filter((h) => h.isArchived));
    } catch {
      // Silently fail — archived section is optional
    } finally {
      setIsLoadingArchived(false);
    }
  };

  const validateName = useCallback((value: string): string | undefined => {
    if (value.trim().length === 0) return 'Name is required';
    if (value.length > 100) return 'Name must be 100 characters or less';
    return undefined;
  }, []);

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setName(value);
      if (nameError) {
        setNameError(validateName(value));
      }
    },
    [nameError, validateName]
  );

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      if (value.length <= 500) {
        setDescription(value);
      }
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      // Validate name
      const error = validateName(name);
      if (error) {
        setNameError(error);
        return;
      }

      // Check capacity
      if (isAtCapacity) {
        setFormError(
          `You've reached the maximum of ${MAX_ACTIVE_HABITS} active habits. Archive an existing habit to create a new one.`
        );
        return;
      }

      setIsSubmitting(true);
      setFormError(null);

      try {
        const habit = await createHabit({
          name: name.trim(),
          description: description || undefined,
          targetTime: targetTime || undefined,
          color: color || undefined,
        });
        onCreated?.(habit);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to create habit';
        setFormError(message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [name, description, targetTime, color, isAtCapacity, createHabit, onCreated, validateName]
  );

  const handleRestore = useCallback(
    async (habitId: string) => {
      setRestoringId(habitId);
      try {
        await updateHabit(habitId, { isArchived: false });
        // Remove from archived list locally
        setArchivedHabits((prev) => prev.filter((h) => h.habitId !== habitId));
        // Reload active habits to include the restored one
        await loadHabits();
      } catch {
        // Error is handled by the store
      } finally {
        setRestoringId(null);
      }
    },
    [updateHabit, loadHabits]
  );

  const handleColorSelect = useCallback((selectedColor: string) => {
    setColor(selectedColor);
  }, []);

  return (
    <Card
      className="w-full max-w-md"
      header={
        <div className="flex items-center justify-between">
          <h2 className="text-heading-3 font-semibold text-text-heading">
            New Habit
          </h2>
          {onCancel && (
            <button
              onClick={onCancel}
              className="p-2 rounded-sm text-text-secondary hover:bg-hover-bg transition-colors duration-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
              aria-label="Close form"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      }
    >
      {/* Capacity error banner */}
      {isAtCapacity && (
        <div
          className="flex items-center gap-2 px-4 py-3 mb-4 rounded-sm bg-primary-dark/10 border border-primary-dark/20"
          role="alert"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-primary-dark flex-shrink-0"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <p className="text-sm text-primary-dark">
            You&apos;ve reached the maximum of {MAX_ACTIVE_HABITS} active habits.
            Archive an existing habit or restore one below.
          </p>
        </div>
      )}

      {/* Form error */}
      {formError && !isAtCapacity && (
        <div
          className="px-4 py-3 mb-4 rounded-sm bg-primary-dark/10 border border-primary-dark/20"
          role="alert"
        >
          <p className="text-sm text-primary-dark">{formError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {/* Name input */}
        <Input
          label="Name"
          value={name}
          onChange={handleNameChange}
          onBlur={() => setNameError(validateName(name))}
          error={nameError}
          maxLength={100}
          placeholder="e.g., Morning meditation"
          aria-required="true"
          disabled={isAtCapacity}
        />

        {/* Description textarea */}
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="habit-form-description"
            className="text-sm font-medium text-text-secondary"
          >
            Description
          </label>
          <textarea
            id="habit-form-description"
            value={description}
            onChange={handleDescriptionChange}
            maxLength={500}
            placeholder="Optional description for your habit"
            rows={3}
            disabled={isAtCapacity}
            className={[
              'w-full rounded-sm border bg-bg-card text-text-primary placeholder:text-text-secondary/60',
              'transition-all duration-fast ease-fast',
              'focus:outline-none focus:ring-2 focus:ring-focus-ring focus:border-secondary',
              'border-border hover:border-secondary-light',
              'disabled:opacity-50 disabled:pointer-events-none',
              'resize-none px-4 py-2 text-body',
            ].join(' ')}
          />
          <span className="text-xs text-text-secondary text-right">
            {description.length}/500
          </span>
        </div>

        {/* Target time picker */}
        <Input
          label="Target Time"
          type="time"
          value={targetTime}
          onChange={(e) => setTargetTime(e.target.value)}
          placeholder="HH:mm"
          disabled={isAtCapacity}
        />

        {/* Color picker */}
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-text-secondary">Color</span>
          <div
            className="flex flex-wrap gap-2"
            role="radiogroup"
            aria-label="Habit color"
          >
            {COLOR_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={color === option}
                aria-label={`Color ${option}`}
                onClick={() => handleColorSelect(option)}
                disabled={isAtCapacity}
                className={[
                  'w-8 h-8 rounded-full border-2 transition-all duration-default',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2',
                  'disabled:opacity-50 disabled:pointer-events-none',
                  color === option
                    ? 'border-text-heading scale-110 shadow-card'
                    : 'border-transparent hover:scale-105',
                ].join(' ')}
                style={{ backgroundColor: option }}
              />
            ))}
          </div>
        </div>

        {/* Submit button */}
        <div className="flex justify-end pt-2">
          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={isAtCapacity || isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Create Habit'}
          </Button>
        </div>
      </form>

      {/* Archived Habits Section */}
      <div className="mt-6 border-t border-border pt-4">
        <button
          type="button"
          onClick={() => setShowArchived(!showArchived)}
          className="flex items-center gap-2 w-full text-left text-sm font-medium text-text-secondary hover:text-text-heading transition-colors duration-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring rounded-sm px-1 py-1"
          aria-expanded={showArchived}
          aria-controls="archived-habits-section"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={[
              'transition-transform duration-default',
              showArchived ? 'rotate-90' : '',
            ].join(' ')}
            aria-hidden="true"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
          Archived Habits
        </button>

        {showArchived && (
          <div
            id="archived-habits-section"
            className="mt-3 flex flex-col gap-2"
            role="list"
            aria-label="Archived habits"
          >
            {isLoadingArchived && (
              <div
                className="flex items-center justify-center py-4"
                aria-busy="true"
                aria-label="Loading archived habits"
              >
                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {!isLoadingArchived && archivedHabits.length === 0 && (
              <p className="text-sm text-text-secondary px-2 py-3">
                No archived habits.
              </p>
            )}

            {!isLoadingArchived &&
              archivedHabits.map((habit) => (
                <div
                  key={habit.habitId}
                  role="listitem"
                  className="flex items-center justify-between px-3 py-2 rounded-sm bg-bg-secondary"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {habit.color && (
                      <span
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: habit.color }}
                        aria-hidden="true"
                      />
                    )}
                    <span className="text-sm text-text-primary truncate">
                      {habit.name}
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRestore(habit.habitId)}
                    disabled={restoringId === habit.habitId || isAtCapacity}
                    aria-label={`Restore habit: ${habit.name}`}
                  >
                    {restoringId === habit.habitId ? 'Restoring...' : 'Restore'}
                  </Button>
                </div>
              ))}
          </div>
        )}
      </div>
    </Card>
  );
}

HabitForm.displayName = 'HabitForm';
